import { useCallback, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CountrySelect,
  InfoBar,
  isTouchDevice,
  Link,
  LogSentryException,
  Modal,
  ServiceError,
  type ServiceErrorResponse,
  StateSelect,
  TextField,
} from "@shapr3d/frontend-lib";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { checkoutBillingErrorContactUsURL } from "../../config";
import { useBilling, useUpdateBillingDetailsMutation } from "../../hooks/useBilling";
import type { BillingDetails } from "../../hooks/useCheckout";
import { useTracker } from "../../hooks/useTracker";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { ServiceErrorCode } from "../../types";
import { getVATNumberPrefix, isCountryInEU } from "../../utils/countryUtils";
import type { DeepMapNativeObjectKeysToValue } from "../../utils/typeUtils";
import { COUNTRIES_WITH_MANDATORY_STATE_SELECTOR } from "../ChargebeeCheckout/ChargebeeCheckoutSchema";

import style from "./BillingDetailsUpdateModal.module.css";

const schema = z
  .object({
    company: z.string().trim().optional(),
    line1: z.string().trim().optional(),
    line2: z.string().trim().optional(),
    city: z.string().trim().optional(),
    countryCode: z.string().optional(),
    zip: z.string().trim().optional(),
    stateCode: z.string().optional(),
    vatNumber: z.string().trim().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.vatNumber && !val.vatNumber?.startsWith(getVATNumberPrefix(val.countryCode))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        fatal: true,
        path: ["vatNumber"],
        message: "Invalid VAT number - Please include your country code",
      });

      return z.NEVER;
    }
  })
  .transform((val) => {
    if (val.vatNumber && isCountryInEU(val.countryCode)) {
      val.vatNumber = val.vatNumber.trim().slice(2);
    }
    return val;
  });

interface BillingDetailsUpdateModalProps {
  teamID: string;
  open: boolean;
  onClose: () => void;
}

export interface UpdateBillingDetailsServiceErrorResponse extends ServiceErrorResponse {
  validationErrors?: DeepMapNativeObjectKeysToValue<z.infer<typeof schema>, { code: string }>;
}

export const BillingDetailsUpdateModal: React.FC<BillingDetailsUpdateModalProps> = ({
  teamID,
  open,
  onClose,
}) => {
  const [isBillingUpdateError, setIsBillingUpdateError] = useState(false);
  const [isUnhandledError, setIsUnhandledError] = useState(false);
  const tracker = useTracker();

  const { data: billingData, isLoading: isBillingLoading } = useBilling(teamID);

  const {
    control,
    register,
    handleSubmit,
    reset,

    formState: { errors },
    setError,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      ...billingData?.billingDetails,
      vatNumber: billingData?.billingDetails?.vatNumber
        ? `${getVATNumberPrefix(billingData.billingDetails.countryCode)}${
            billingData.billingDetails.vatNumber
          }`
        : undefined,
    },
    resolver: zodResolver(schema),
  });

  const countryCode = useWatch({ control, name: "countryCode" });

  const { mutate, isPending } = useUpdateBillingDetailsMutation(teamID);

  const close = useCallback(
    (values?: BillingDetails) => {
      values
        ? reset({
            ...values,
            vatNumber: values.vatNumber
              ? `${getVATNumberPrefix(values.countryCode)}${values.vatNumber}`
              : undefined,
          })
        : reset();
      onClose();
    },
    [onClose, reset]
  );

  const submit = useCallback(
    async (data: z.infer<typeof schema>) => {
      Object.entries(data).forEach(([key, value]) => {
        if (!value) {
          delete data[key as keyof typeof data];
        }
      });
      tracker.trackButtonClick({
        action: ElementClickAction.CONFIRM,
        location: ElementClickLocation.EDIT_BILLING_DETAILS_MODAL,
      });
      mutate(data, {
        onSuccess() {
          close(data);
        },
        onError(error) {
          if (error instanceof ServiceError && error.hasCode(ServiceErrorCode.ValidationError)) {
            const err: ServiceError = error;
            err.assertType<UpdateBillingDetailsServiceErrorResponse>();
            const validationErrors = Object.keys(err.serviceErrorResponse?.validationErrors ?? {});

            if (validationErrors.length > 0) {
              validationErrors.forEach((key) => {
                let message = "Something went wrong.";
                switch (key as keyof typeof data) {
                  case "company":
                    message = "Invalid name";
                    break;
                  case "line1":
                    message = "Invalid address";
                    break;
                  case "line2":
                    message = "Invalid address";
                    break;
                  case "city":
                    message = "Invalid city";
                    break;
                  case "countryCode":
                    LogSentryException({
                      error: new Error("Invalid country during billing update"),
                      level: "error",
                    });
                    message = "Invalid country";
                    break;
                  case "zip":
                    message = "Invalid post code";
                    break;
                  case "stateCode":
                    LogSentryException({
                      error: new Error("Invalid state during billing update"),
                      level: "error",
                    });
                    message = "Invalid state";
                    break;
                  case "vatNumber":
                    message = "Invalid VAT number";
                    break;
                  default:
                    LogSentryException({
                      error: new Error("Unknown error field during billing update", { cause: key }),
                      level: "error",
                    });
                    setIsUnhandledError(true);
                    return;
                }

                setError(key as keyof typeof data, {
                  message,
                  type: "validate",
                });
              });
              return;
            }

            setIsBillingUpdateError(true);
            return;
          }

          setIsUnhandledError(true);
        },
      });
    },
    [mutate, close, setIsBillingUpdateError, setIsUnhandledError, setError, tracker]
  );

  if (isBillingLoading) {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        close();
      }}
      title="Edit billing details"
    >
      <form className={style.form} onSubmit={handleSubmit(submit)}>
        <TextField
          {...register("company")}
          maxLength={250}
          errorMessage={errors.company?.message}
          placeholder="Name on the bill"
          autoFocus={!isTouchDevice()}
        />
        <TextField
          {...register("line1")}
          maxLength={150}
          errorMessage={errors.line1?.message}
          placeholder="Address Line 1"
        />
        <TextField
          {...register("line2")}
          maxLength={150}
          errorMessage={errors.line2?.message}
          placeholder="Address Line 2"
        />
        <TextField
          {...register("city")}
          maxLength={50}
          errorMessage={errors.city?.message}
          placeholder="City"
        />
        <div className={style.countryRow}>
          <CountrySelect {...register("countryCode")} errorMessage={errors.countryCode?.message} />
          <TextField
            {...register("zip")}
            maxLength={20}
            errorMessage={errors.zip?.message}
            placeholder="Post code"
          />
        </div>
        {countryCode && COUNTRIES_WITH_MANDATORY_STATE_SELECTOR.includes(countryCode) && (
          <StateSelect
            {...register("stateCode")}
            errorMessage={errors.stateCode?.message}
            countryCode={countryCode as Parameters<typeof StateSelect>[0]["countryCode"]}
          />
        )}
        <TextField
          {...register("vatNumber")}
          placeholder={
            isCountryInEU(countryCode) ? "VAT number (including country code)" : "Tax ID"
          }
          errorMessage={errors.vatNumber?.message}
        />
        {isBillingUpdateError && (
          <InfoBar
            variant="critical"
            title="Billing address issue"
            text={
              <>
                <span>
                  {`${"We couldn\u2019t verify your billing information. Please check your details and try again. Let us know if we can help if the issue persists. "}`}
                </span>
                <Link href={checkoutBillingErrorContactUsURL} target="_blank">
                  Contact Us
                </Link>
              </>
            }
          />
        )}
        {isUnhandledError && (
          <InfoBar
            variant="warning"
            title={"Updating billing address is unavailable at the moment"}
            text="Please reload the page or try again later if the issue persists."
          />
        )}
        <div className={style.ctaRow}>
          <Button type="submit" disabled={isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};
